import { useState } from 'react'
import ReactDOM from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'

import Button from '../../../common/Button'
import Modal from '../../../common/Modal'
import ModalInnerWrapper from '../../../common/ModalInnerWrapper'
import PannelBody from '../../../fragments/PannelBody'
import Backdrop from '../../../common/modal/Backdrop'
import BodySimpleModalV3 from '../../../common/modal/BodySimpleModalV3'
import FormTextareaBox from '../../../common/interactive/FormTextareaBox'
import CheckboxCell from '../../../common/interactive/CheckboxCell'
import { deleteCaseDocumentApi } from '../../../../lib/fetchApi'
import { notificationActions } from '../../../../store/notifications'
import { caseActions } from '../../../../store/case'

import classes from './DocumentsTabListItem.module.css'

const MIN_REASON_LENGTH = 20

export default function DeleteDocument({ document: documentItem, hardDeletePermission }) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase)

    const [loading, setLoading] = useState(false)
    const [modalOpened, setModalOpened] = useState(false)
    const [reason, setReason] = useState('')
    const [reasonError, setReasonError] = useState('')
    const [hardDelete, setHardDelete] = useState(false)

    const openModalHandler = (event) => {
        event.preventDefault()
        event.stopPropagation()
        setReason('')
        setReasonError('')
        setHardDelete(false)
        setModalOpened(true)
    }

    const closeModalHandler = (event) => {
        event?.preventDefault?.()
        event?.stopPropagation?.()
        setModalOpened(false)
        setReason('')
        setReasonError('')
        setHardDelete(false)
    }

    const deleteHandler = async (event) => {
        event.preventDefault()
        event.stopPropagation()

        const trimmedReason = reason.trim()
        if (trimmedReason.length < MIN_REASON_LENGTH) {
            setReasonError(`Please enter at least ${MIN_REASON_LENGTH} characters.`)
            return
        }

        if (loading) {
            return
        }

        setLoading(true)
        try {
            const result = await deleteCaseDocumentApi(
                userToken,
                activeCase.case.id,
                documentItem.id,
                trimmedReason,
                dispatch,
                hardDeletePermission && hardDelete
            )

            if (result && result.errorMessage) {
                dispatch(notificationActions.setError(result))
            } else if (result) {
                dispatch(caseActions.replaceWholeItemInDocuments({ payload: result }))
                dispatch(notificationActions.setError({ errorMessage: 'success', type: 'success' }))
                closeModalHandler()
            }
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            <BodySimpleModalV3 message="Delete document">
                <Button
                    btnStyle="btn_simple"
                    color="danger"
                    click={openModalHandler}
                    type="button"
                >
                    <i className="ti ti-trash" />
                    <span className="sr-only">Delete document</span>
                </Button>
            </BodySimpleModalV3>
            {modalOpened && (
                <>
                    {ReactDOM.createPortal(
                        <Backdrop hide={closeModalHandler} />,
                        document.getElementById('backdrop-root')
                    )}
                    {ReactDOM.createPortal(
                        <Modal>
                            <ModalInnerWrapper closeModal={closeModalHandler} modalTitle="Delete document?">
                                <PannelBody>
                                    <form onSubmit={deleteHandler}>
                                        <p className="font-14">
                                            This document will be removed from active use and shown in the deleted list at the bottom.
                                        </p>
                                        {hardDeletePermission && hardDelete ? (
                                            <p className={`font-14 m-top-12 ${classes.hard_delete_warning}`}>
                                                <strong>Hard delete:</strong> The file will be permanently removed from Box. This document cannot be restored.
                                            </p>
                                        ) : (
                                            <p className="font-14 m-top-12">
                                                Related case flags will be rechecked based on remaining documents.
                                            </p>
                                        )}
                                        {hardDeletePermission && (
                                            <div className={classes.delete_checkbox_spacing}>
                                                <CheckboxCell
                                                    label="Also remove file from Box (cannot be restored)"
                                                    name="hard_delete_case_document"
                                                    direction="reverse"
                                                    checked={hardDelete}
                                                    trueFlagDanger={true}
                                                    action={(payload) => setHardDelete(Boolean(payload?.data?.value))}
                                                />
                                            </div>
                                        )}
                                        <div className={classes.delete_textarea_spacing}>
                                            <FormTextareaBox
                                                id="delete_case_document_reason"
                                                name="reason"
                                                label="Reason for deletion"
                                                value={reason}
                                                onChange={(e) => {
                                                    setReason(e.target.value)
                                                    if (reasonError) {
                                                        setReasonError('')
                                                    }
                                                }}
                                                required
                                                helperText={`${reason.trim().length}/${MIN_REASON_LENGTH} characters minimum`}
                                                errorText={reasonError}
                                            />
                                        </div>
                                        <div className={`flex-container-equal m-top-24 ${classes.delete_document_actions}`}>
                                            <Button
                                                btnStyle="btn_danger"
                                                type="submit"
                                                isLoading={loading}
                                                isDisabled={reason.trim().length < MIN_REASON_LENGTH}
                                            >
                                                <i className="ti ti-trash" /> {hardDelete ? 'Yes, hard delete' : 'Yes, delete'}
                                            </Button>
                                            <Button click={closeModalHandler} type="button">
                                                Cancel
                                            </Button>
                                        </div>
                                    </form>
                                </PannelBody>
                            </ModalInnerWrapper>
                        </Modal>,
                        document.getElementById('overlay-root')
                    )}
                </>
            )}
        </>
    )
}
